import "../step.js";
import "./insurance/fields/subscriber_date_of_birth_field.js";
import "./insurance/callouts/validation_error_callout.js";

window.AppointmentBooking ??= {};
window.AppointmentBooking.Wizard ??= {};
window.AppointmentBooking.Wizard.Views ??= {};

const __InsuranceStepPrior = AppointmentBooking.Wizard.Views.InsuranceStep;
AppointmentBooking.Wizard.Views.InsuranceStep = class InsuranceStep extends AppointmentBooking.Wizard.Views.Step {
  constructor() {
    super(...arguments);
    this.recoverableErrorTypes = this.recoverableErrorTypes.bind(this);
    this.beforeShow = this.beforeShow.bind(this);
    this.areRequirementsValid = this.areRequirementsValid.bind(this);
    this.isDisabled = this.isDisabled.bind(this);
    this.initialize = this.initialize.bind(this);
    this.handleInsuranceUpdateResponseChange = this.handleInsuranceUpdateResponseChange.bind(this);
    this.handleNameChange = this.handleNameChange.bind(this);
    this.handleDateOfBirthChange = this.handleDateOfBirthChange.bind(this);
    this.handleSubscriberIsPatientChange = this.handleSubscriberIsPatientChange.bind(this);
    this.render = this.render.bind(this);
    this.copyNameToSubscriberName = this.copyNameToSubscriberName.bind(this);
    this.copyDateOfBirthToSubscriber = this.copyDateOfBirthToSubscriber.bind(this);
    this.getPageTitle = this.getPageTitle.bind(this);
    this.triggerShowDetailsHelpAction = this.triggerShowDetailsHelpAction.bind(this);
    this.clearInsuranceValidationState = this.clearInsuranceValidationState.bind(this);
    this.updateFieldVisibility = this.updateFieldVisibility.bind(this);
    this.updateResponseVisibility = this.updateResponseVisibility.bind(this);
    this.areCustomInsuranceDetailsComplete = this.areCustomInsuranceDetailsComplete.bind(this);
    this.areExistingInsuranceDetailsComplete = this.areExistingInsuranceDetailsComplete.bind(this);
    this.areCommonInsuranceDetailsComplete = this.areCommonInsuranceDetailsComplete.bind(this);
    this.areExtendedSubscriberDetailsComplete = this.areExtendedSubscriberDetailsComplete.bind(this);
    this.isNoSpecificInsuranceAllowed = this.isNoSpecificInsuranceAllowed.bind(this);
    this.noSpecificInsuranceSelected = this.noSpecificInsuranceSelected.bind(this);
    this.specialPayors = this.specialPayors.bind(this);
  }

  recoverableErrorTypes() {
    return this.validationErrorCallout.getRecoverableErrorTypes();
  }

  beforeShow() {
    return this.handleSubscriberIsPatientChange();
  }

  areRequirementsValid() {
    if (this.model.insuranceUpdateQuestionEnabled()) {
      if (this.model.get("insurance_update_response") == null) {
        return false;
      }
      if (this.model.insuranceReportedUnchanged()) {
        return true;
      }
    }

    const payorId = this.model.get("payor_id");
    if (!payorId) {
      return false;
    } // Nothing selected yet
    if (this.noSpecificInsuranceSelected()) {
      return this.isNoSpecificInsuranceAllowed();
    }
    if (payorId === this.specialPayors().not_listed) {
      return this.areCustomInsuranceDetailsComplete();
    }
    return this.areExistingInsuranceDetailsComplete();
  }

  isDisabled() {
    return this.model.insuranceCollectionSkipped();
  }

  initialize() {
    super.initialize(...arguments);

    this.validationErrorCallout = new AppointmentBooking.Wizard.Views.InsuranceStep.ValidationErrorCallout({
      model: this.model,
      condition: () => this.validationErrorCallout.hasRecoverableErrors(),
    });

    this.noInsuranceCallout = new AppointmentBooking.Wizard.Views.Callout({
      className: "appointment-booking-wizard-insurance-step-no-insurance-callout borderless",
      model: this.model,
      template: "step/insurance/callouts/no_insurance",
      condition: () => this.noSpecificInsuranceSelected() && !this.isNoSpecificInsuranceAllowed(),
    });

    this.detailsHelpCallout = new AppointmentBooking.Wizard.Views.Callout({
      className: "appointment-booking-wizard-insurance-step-details-help-callout borderless with-preceding",
      template: "step/insurance/callouts/details_help",
      analytics: "message-shown",
    });
    this.detailsHelpCallout.hide();

    this.subscriberDateOfBirthField = new AppointmentBooking.Wizard.Views.InsuranceStep.SubscriberDateOfBirthField({
      model: this.model,
    });

    // BEHAVIOUR

    this.listenTo(this.model, "change:payor_id", this.updateFieldVisibility);
    this.listenTo(this.model, "change:insurance_update_response", this.handleInsuranceUpdateResponseChange);
    this.listenTo(this.model, "change:first_name", this.handleNameChange);
    this.listenTo(this.model, "change:last_name", this.handleNameChange);
    this.listenTo(this.model, "change:date_of_birth", this.handleDateOfBirthChange);
    return this.listenTo(this.model, "change:subscriber_is_patient", this.handleSubscriberIsPatientChange);
  }

  // EVENT HANDLERS

  handleShowDetailsHelpClick() {
    return this.triggerShowDetailsHelpAction();
  }

  // Updates in place so the answer radios survive: an arrow key both moves focus and checks the next answer, so
  // replacing those elements here drops focus and ends the group's native keyboard navigation after one keypress.
  handleInsuranceUpdateResponseChange() {
    this.clearInsuranceValidationState();
    this.updateFieldVisibility();
    return this.updateResponseVisibility();
  }

  handleNameChange() {
    if (this.model.get("subscriber_is_patient")) {
      return this.copyNameToSubscriberName();
    }
  }

  handleDateOfBirthChange() {
    if (this.model.get("subscriber_is_patient")) {
      return this.copyDateOfBirthToSubscriber();
    }
  }

  handleSubscriberIsPatientChange() {
    if (this.model.get("subscriber_is_patient")) {
      this.copyNameToSubscriberName();
      this.copyDateOfBirthToSubscriber();
    } else {
      this.model.set({ subscriber_name: null });
      this.model.set({ subscriber_date_of_birth: null });
    }
    return this.render();
  }

  // RENDERING

  render() {
    if (!this.renderable) {
      return this;
    }

    super.render(...arguments);
    this.replaceContents(".insurance-recoverable-error-container", this.validationErrorCallout.render().el);
    this.replaceContents(".no-insurance-callout-container", this.noInsuranceCallout.render().el);
    this.replaceContents(".insurance-details-help-callout-container", this.detailsHelpCallout.render().el);
    this.renderField(".subscriber-date-of-birth-field", this.subscriberDateOfBirthField);
    this.updateFieldVisibility();
    this.updateResponseVisibility();

    return this;
  }

  // HELPER FUNCTIONS

  copyNameToSubscriberName() {
    return this.model.set({ subscriber_name: this.model.get("first_name") + " " + this.model.get("last_name") });
  }

  copyDateOfBirthToSubscriber() {
    return this.model.set({ subscriber_date_of_birth: this.model.get("date_of_birth") });
  }

  getPageTitle() {
    return `${this.I18n().insurance.page_title} - ${this.model.get("appointment_slot").visit_type_name} - ${this.I18n().login_type[this.model.get("login_type")]}`;
  }

  triggerShowDetailsHelpAction() {
    return this.detailsHelpCallout.toggle();
  }

  // PRIVATE

  clearInsuranceValidationState() {
    this.ensureRendered();
    return this.$(".insurance-form-section, .insurance-form-section .field").removeClass("dirty valid invalid");
  }

  updateResponseVisibility() {
    if (!this.model.insuranceUpdateQuestionEnabled()) {
      return;
    }
    this.ensureRendered();

    const response = this.model.get("insurance_update_response");
    this.$(".insurance-form-section").toggle(response === "changed");
    this.$(".insurance-update-response-message--unchanged").toggle(response === "unchanged");
    this.$(".insurance-update-response-message--self-pay").toggle(response === "self_pay");
  }

  updateFieldVisibility() {
    this.ensureRendered();
    const insuranceDetailsFields = this.assert.element(this.$(".insurance-details-fields"));
    const insuranceNameField = this.assert.element(this.$(".insurance-name-field"));

    switch (this.model.get("payor_id")) {
      case null:
      case "": // Nothing selected yet
        return insuranceDetailsFields.hide();
      case this.specialPayors().no_insurance: // No Insurance
        return insuranceDetailsFields.hide();
      case this.specialPayors().not_listed: // Insurance not listed
        insuranceDetailsFields.show();
        return insuranceNameField.show();
      default:
        insuranceDetailsFields.show();
        return insuranceNameField.hide();
    }
  }

  areCustomInsuranceDetailsComplete() {
    return this.isAttributeInputValid("insurance_name") && this.areCommonInsuranceDetailsComplete();
  }

  areExistingInsuranceDetailsComplete() {
    return this.model.get("payor_id") && this.areCommonInsuranceDetailsComplete();
  }

  areCommonInsuranceDetailsComplete() {
    return (
      this.isAttributeInputValid("member_number") &&
      this.isAttributeInputValid("group_number") &&
      this.areExtendedSubscriberDetailsComplete()
    );
  }

  areExtendedSubscriberDetailsComplete() {
    if (!AppointmentBooking.config.collect_extended_subscriber_insurance_details) {
      return true;
    }

    return (
      this.isAttributeInputValid("subscriber_id") &&
      this.isAttributeInputValid("subscriber_name") &&
      this.isAttributeInputValid("subscriber_date_of_birth")
    );
  }

  isNoSpecificInsuranceAllowed() {
    return !!this.model.get("visit_type")?.allow_no_specific_insurance;
  }

  noSpecificInsuranceSelected() {
    const payorId = this.model.get("payor_id");
    return payorId === this.specialPayors().no_insurance || payorId === this.specialPayors().no_selection;
  }

  specialPayors() {
    return AppointmentBooking.config.special_payors;
  }
};
AppointmentBooking.Wizard.Views.InsuranceStep.prototype.template = "step/insurance";
AppointmentBooking.Wizard.Views.InsuranceStep.prototype.name = "Insurance";
AppointmentBooking.Wizard.Views.InsuranceStep.prototype.className = "appointment-booking-wizard-insurance-step";

AppointmentBooking.Wizard.Views.InsuranceStep.prototype.events = {
  'click [data-appointment-booking-wizard-behaviour="show-insurance-details-help"]': "handleShowDetailsHelpClick",
};

if (__InsuranceStepPrior) {
  Object.assign(AppointmentBooking.Wizard.Views.InsuranceStep, __InsuranceStepPrior);
}
